import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import {useSnackbar} from 'notistack';

import {useApplicationContext} from 'ApplicationContext';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';

import {LabelEmployee, LabelTask} from './labelTaskTypes';
import {LabelwinCustomer, customerLabel, useCustomerSearch, useLookup} from './labelwinMasterData';

interface Props {
    open: boolean;
    /** Die zu bearbeitende Aufgabe, oder `null` für „neu anlegen". */
    task: LabelTask | null;
    /** Mitarbeiter für die Zuweisungs-Auswahl; leer, wenn der Benutzer nicht zuweisen darf. */
    employees: LabelEmployee[];
    /** Darf der Benutzer Aufgaben für andere anlegen bzw. umzuweisen? */
    canAssign: boolean;
    onClose: () => void;
    /** Nach erfolgreichem Speichern, zum Neuladen der Liste. */
    onSaved: (task: LabelTask) => void;
}

/**
 * Eine Aufgabe anlegen oder bearbeiten.
 *
 * Beim Bearbeiten gehen nur die **geänderten** Felder als PATCH hinaus — der Server ändert nur
 * übergebene Felder, und so bleibt alles Übrige garantiert unangetastet. Eine gesetzte Fälligkeit
 * lässt sich über die Schnittstelle nicht wieder leeren; der Hinweis dazu steht am Feld.
 */
export const TaskEditDialog: React.FC<Props> = ({open, task, employees, canAssign, onClose, onSaved}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [subject, setSubject] = useState('');
    const [text, setText] = useState('');
    const [remark, setRemark] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [priority, setPriority] = useState('');
    const [activityId, setActivityId] = useState<number | ''>('');
    const [assignee, setAssignee] = useState<LabelEmployee | null>(null);
    const [customer, setCustomer] = useState<LabelwinCustomer | null>(null);
    const [customerTerm, setCustomerTerm] = useState('');
    const [busy, setBusy] = useState(false);

    const activities = useLookup(context.api, 'Activities');
    const {customers, loading: searching} = useCustomerSearch(context.api, customerTerm);

    useEffect(() => {
        if (!open) return;
        setSubject(task?.subject ?? '');
        setText(task?.text ?? '');
        setRemark(task?.remark ?? '');
        setDueDate(task?.dueDate ?? '');
        setPriority(task ? String(task.priority) : '');
        setActivityId(task?.activityId ?? '');
        setAssignee(task ? employees.find((e) => e.employeeId === task.employeeId) ?? null : null);
        setCustomer(null);
        setCustomerTerm('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, task]);

    const save = async (): Promise<void> => {
        setBusy(true);
        try {
            if (task === null) {
                const r = await http.post<LabelTask>('/Tasks', undefined, {
                    // Ohne Auswahl entsteht die Aufgabe für den angemeldeten Benutzer.
                    employeeId: assignee?.employeeId ?? null,
                    subject: subject.trim(),
                    text: text.trim() || null,
                    remark: remark.trim() || null,
                    dueDate: dueDate || null,
                    priority: priority ? Number(priority) : null,
                    activityId: activityId === '' ? null : activityId,
                    // Der Kunde macht die Aufgabe später in einen Auftrag umwandelbar.
                    customerId: customer?.customerId ?? null,
                });
                enqueueSnackbar(
                    assignee
                        ? t('labelTasks.createdFor', {name: assignee.name ?? assignee.employeeId})
                        : t('labelTasks.created'),
                    {variant: 'success'});
                onSaved(r.data);
            } else {
                // Nur geänderte Felder — ein leeres Fälligkeitsfeld heißt „unverändert lassen",
                // nicht „löschen": das kann die Schnittstelle nicht.
                const patch: Record<string, unknown> = {};
                if (subject.trim() !== (task.subject ?? '')) patch.subject = subject.trim();
                if (text.trim() !== (task.text ?? '').trim()) patch.text = text.trim();
                if (remark.trim() !== (task.remark ?? '').trim()) patch.remark = remark.trim();
                if (dueDate && dueDate !== (task.dueDate ?? '')) patch.dueDate = dueDate;
                if (priority && Number(priority) !== task.priority) patch.priority = Number(priority);
                if (activityId !== '' && activityId !== task.activityId) patch.activityId = activityId;
                if (assignee && assignee.employeeId !== task.employeeId) patch.employeeId = assignee.employeeId;

                if (Object.keys(patch).length === 0) {
                    onClose();
                    return;
                }

                const r = await http.patch<LabelTask>(`/Tasks/${task.id}`, patch);
                enqueueSnackbar(t('labelTasks.saved'), {variant: 'success'});
                onSaved(r.data);
            }
            onClose();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('labelTasks.saveFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{task === null ? t('labelTasks.new') : t('labelTasks.edit')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    {canAssign && (
                        <Autocomplete
                            size="small"
                            options={employees}
                            value={assignee}
                            onChange={(_, value) => setAssignee(value)}
                            getOptionLabel={(option) => option.name ?? option.labelUser ?? String(option.employeeId)}
                            isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                            renderInput={(params) => (
                                <TextField {...params} label={t('labelTasks.field.assignee')}
                                           helperText={task === null ? t('labelTasks.assigneeHint') : undefined}/>
                            )}
                        />
                    )}

                    <TextField autoFocus fullWidth size="small" label={t('labelTasks.field.subject')}
                               value={subject}
                               slotProps={{htmlInput: {maxLength: 100}}}
                               onChange={(e) => setSubject(e.target.value)}/>

                    <TextField fullWidth size="small" label={t('labelTasks.field.text')}
                               multiline minRows={3}
                               value={text}
                               onChange={(e) => setText(e.target.value)}/>

                    {/* Die Anmerkung ist das Feld, in dem der Labelwin-Client festhält, was
                        tatsächlich zu tun ist — unbegrenzt und mehrzeilig. */}
                    <TextField fullWidth size="small" label={t('labelTasks.field.remark')}
                               multiline minRows={2}
                               value={remark}
                               onChange={(e) => setRemark(e.target.value)}/>

                    {activities.length > 0 && (
                        <TextField select fullWidth size="small" label={t('labelTasks.field.activity')}
                                   value={activityId}
                                   onChange={(e) => setActivityId(
                                       e.target.value === '' ? '' : Number(e.target.value))}>
                            <MenuItem value="">{t('labelTasks.useDefault')}</MenuItem>
                            {activities.map((value) => (
                                <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                            ))}
                        </TextField>
                    )}

                    {/*
                      * Kunde nur beim Anlegen: er entscheidet, ob aus der Aufgabe später ein
                      * Kundendienst-Auftrag werden kann. Beim Bearbeiten bleibt er, wie Labelwin
                      * ihn führt — ihn hier umzuhängen wäre eine andere Handlung.
                      */}
                    {task === null && (
                        <Autocomplete
                            size="small"
                            options={customers}
                            value={customer}
                            loading={searching}
                            onChange={(_, value) => setCustomer(value)}
                            onInputChange={(_, value) => setCustomerTerm(value)}
                            getOptionLabel={customerLabel}
                            isOptionEqualToValue={(a, b) => a.customerId === b.customerId}
                            filterOptions={(options) => options}
                            noOptionsText={t('labelTasks.customerHint')}
                            loadingText={t('labelTasks.searching')}
                            renderInput={(params) => (
                                <TextField {...params} label={t('labelTasks.field.customer')}
                                           helperText={t('labelTasks.customerOptionalHint')}/>
                            )}
                        />
                    )}

                    <TextField fullWidth size="small" type="date" label={t('labelTasks.field.dueDate')}
                               value={dueDate}
                               slotProps={{inputLabel: {shrink: true}}}
                               onChange={(e) => setDueDate(e.target.value)}
                               helperText={task?.dueDate ? t('labelTasks.hint.dueDateNotClearable') : undefined}/>

                    <TextField fullWidth size="small" type="number" label={t('labelTasks.field.priority')}
                               value={priority}
                               slotProps={{htmlInput: {min: 0, step: 1}}}
                               onChange={(e) => setPriority(e.target.value)}
                               helperText={t('labelTasks.priorityHint')}/>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{t('labelTasks.cancel')}</Button>
                <Button variant="contained" onClick={() => void save()}
                        disabled={busy || !subject.trim()}>
                    {task === null ? t('labelTasks.create') : t('labelTasks.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TaskEditDialog;
